import { useEffect } from "react";

/**
 * Guards a flow (e.g. a multi-step wizard) against the browser Back button.
 *
 * While `active`, a history sentinel is pinned so a Back press lands on the
 * same URL instead of unwinding the route. Each press asks for confirmation:
 * cancel re-pins the sentinel (so repeated presses keep being caught and the
 * user stays put), confirm runs `onConfirm` (e.g. navigate away). The user can
 * only leave by an explicit confirmation, never by a stray Back press.
 */
export const useBackGuard = (
  active: boolean,
  confirmMessage: string,
  onConfirm: () => void,
): void => {
  useEffect(() => {
    if (!active) return;

    // Pin a same-URL entry so the first Back press is caught here, not on the route.
    window.history.pushState(null, "");

    const onPopState = (): void => {
      if (window.confirm(confirmMessage)) {
        window.removeEventListener("popstate", onPopState);
        onConfirm();
      } else {
        // Stay: re-pin so the guard survives repeated Back presses.
        window.history.pushState(null, "");
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [active, confirmMessage, onConfirm]);
};
